import { useState, useEffect } from 'react';
import { useUserProfileContext } from '../../contexts/UserProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/axios';

// Inline provider icons
function IconGoogle({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="#4285F4" d="M12 11.5v2.8h3.9c-.2 1.2-1.2 3.4-3.9 3.4-2.4 0-4.4-2-4.4-4.4s2-4.4 4.4-4.4c1.4 0 2.3.6 2.8 1.1l1.9-1.8C16.5 6.1 14.6 5 12 5 7.6 5 4 8.6 4 13s3.6 8 8 8c4.6 0 7.7-3.2 7.7-7.7 0-.6-.1-1.1-.2-1.6H12z" />
      <path fill="#34A853" d="M4 13c0 1 .3 1.9.8 2.7L6.9 14.6C6.6 14 6.5 13.5 6.5 13s.1-1 .4-1.6L4.8 9.9C4.3 10.7 4 11.8 4 13z" />
      <path fill="#FBBC05" d="M12 5c1.6 0 3 .6 4 1.6l-2 1.9C13.8 8 12.9 7.6 12 7.6 10 7.6 8.3 9 7.7 11.1L4.8 9.9C5.6 7 8.4 5 12 5z" />
      <path fill="#EA4335" d="M19.7 7.9L17.8 9.8C17.2 9.1 15.9 7.6 12 7.6v-2.6c2.6 0 4.5 1.1 5.7 2.9z" />
    </svg>
  );
}

function IconApple({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="currentColor" d="M16.365 1.43c-.9.1-1.98.6-2.6 1.4-.56.7-1.06 1.8-.8 2.8 1.1.1 2.3-.6 3.1-1.4.6-.6 1-1.6.3-3zM12.5 5.3c-1.6 0-3.3.9-4.2.9-.9 0-2.3-1-3.8-1C2.4 5.2 1 7.1 1 10.5c0 1.3.2 2.6.6 3.7 1 2.7 3.4 6.8 6.7 6.7 1.3 0 1.8-.8 3.4-.8 1.6 0 2 .8 3.4.8 3.3.1 5.6-4 6.7-6.7.4-1.2.6-2.5.6-3.7C23 7.1 21.6 5.2 18.8 5.2c-1.5 0-2.9 1-3.8 1-1 0-2.6-.9-4.2-.9z" />
    </svg>
  );
}

function IconGithub({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="currentColor" d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.38 7.86 10.9.58.11.79-.25.79-.56 0-.28-.01-1.02-.02-2-3.2.7-3.88-1.4-3.88-1.4-.53-1.35-1.3-1.71-1.3-1.71-1.06-.72.08-.71.08-.71 1.17.08 1.78 1.2 1.78 1.2 1.04 1.78 2.72 1.27 3.38.97.11-.76.41-1.27.74-1.56-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.27 1.19-3.07-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11.07 11.07 0 0 1 2.9-.39c.98 0 1.97.13 2.9.39 2.2-1.49 3.17-1.18 3.17-1.18.63 1.58.23 2.75.11 3.04.74.8 1.19 1.82 1.19 3.07 0 4.4-2.69 5.36-5.25 5.65.42.36.8 1.08.8 2.18 0 1.57-.01 2.83-.01 3.22 0 .31.21.68.8.56C20.71 21.38 24 17.08 24 12c0-6.27-5.23-11.5-12-11.5z" />
    </svg>
  );
}

export default function Overview() {
  const [linkedApps, setLinkedApps] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [linkedIdentities, setLinkedIdentities] = useState([]);

  const [revokeLoading, setRevokeLoading] = useState(null);
  const [terminateLoading, setTerminateLoading] = useState(null);
  
  // Pagination
  const [linkedAppsCurrentPage, setLinkedAppsCurrentPage] = useState(1);
  const [auditLogCurrentPage, setAuditLogCurrentPage] = useState(1);
  const [auditLogTotalPages, setAuditLogTotalPages] = useState(1);
  const itemsPerPage = 5;

  const { profileData } = useUserProfileContext();
  const { activeIndex, user } = useAuth();
  const activeLocks = (profileData && (profileData.active_locks || profileData.activeLocks)) || [];

  const getAppLockInfo = (clientId) => {
    return activeLocks.find((lock) => lock.client_id === clientId && lock.restrictions && lock.restrictions.includes('prevent_revocation'));
  };

  useEffect(() => {
    // Clear old data when switching accounts to prevent stale UI
    setLinkedApps([]);
    setActiveSessions([]);
    setLinkedIdentities([]);
    setAuditLog([]);
    setAuditLogCurrentPage(1);

    const controller = new AbortController();

    fetchLinkedApps(controller.signal);
    fetchActiveSessions(controller.signal);
    fetchSocialIdentities(controller.signal);

    return () => {
      controller.abort();
    };
  }, [activeIndex, user?.id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAuditLog(auditLogCurrentPage, controller.signal);
    return () => {
      controller.abort();
    };
  }, [auditLogCurrentPage, activeIndex, user?.id]);

  const fetchLinkedApps = async (signal) => {
    try {
      const { data } = await api.get('/users/me/security/linked-apps', { signal });
      setLinkedApps(data.linked_apps || []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load linked apps', err);
    }
  };

  const fetchActiveSessions = async (signal) => {
    try {
      const { data } = await api.get('/users/me/security/sessions', { signal });
      setActiveSessions(data.sessions || []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load sessions', err);
    }
  };

  const fetchSocialIdentities = async (signal) => {
    try {
      const { data } = await api.get('/users/me/security/social-identities', { signal });
      setLinkedIdentities(data.social_identities || []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load social identities', err);
    }
  };

  const fetchAuditLog = async (page, signal) => {
    try {
      const { data } = await api.get(`/users/me/security/audit-logs?per_page=${itemsPerPage}&page=${page}`, { signal });
      setAuditLog(data.audit_logs || []);
      setAuditLogTotalPages(data.pagination?.total_pages || 1);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load audit logs', err);
    }
  };

  const handleRevokeAccess = async (appId, appName, clientId) => {
    const lock = getAppLockInfo(clientId);
    if (lock) {
      alert(`⚠️ Cannot revoke access to "${appName}"\n\nReason: ${lock.reason}\n\nPlease resolve the operational hold with ${lock.client_name} to regain control.`);
      return;
    }
    
    setRevokeLoading(appId);
    try {
      await api.delete(`/users/me/security/linked-apps/${appId}`);
      setLinkedApps(prev => prev.filter(app => app.id !== appId));
    } catch (err) {
      console.error('Failed to revoke app access', err);
      alert('Failed to revoke access. Please try again.');
    } finally {
      setRevokeLoading(null);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    setTerminateLoading(sessionId);
    try {
      await api.delete(`/users/me/security/sessions/${sessionId}`);
      setActiveSessions(prev => prev.filter(s => s.session_id !== sessionId && s.id !== sessionId));
    } catch (err) {
      console.error('Failed to terminate session', err);
      alert('Failed to terminate session.');
    } finally {
      setTerminateLoading(null);
    }
  };

  const unlinkIdentity = async (identityId) => {
    if (linkedIdentities.length === 1) {
      alert('Cannot unlink your last identity. You must have at least one login method.');
      return;
    }

    try {
      await api.delete(`/users/me/security/social-identities/${identityId}`);
      setLinkedIdentities(prev => prev.filter(id => id.id !== identityId));
    } catch (err) {
      console.error('Failed to unlink identity', err);
      alert('Failed to unlink identity.');
    }
  };

  const linkAnotherAccount = (provider) => {
    // We open the popup login flow for this specific provider
    // Real implementation would pass provider to backend for redirect URL
    alert(`This would open the standard popup login flow to link ${provider}.`);
  };

  // Pagination for Linked Apps (Client side)
  const linkedAppsStartIndex = (linkedAppsCurrentPage - 1) * itemsPerPage;
  const linkedAppsPaginated = linkedApps.slice(linkedAppsStartIndex, linkedAppsStartIndex + itemsPerPage);
  const linkedAppsTotalPages = Math.max(1, Math.ceil(linkedApps.length / itemsPerPage));

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Security & Overview</h1>
        <p className="text-slate-600 dark:text-slate-400">Monitor your account's connected applications, active sessions, and security events</p>
      </header>

      {/* Identity Providers */}
      <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Identity Providers & Social Linking</h2>

        <div className="space-y-4">
          {linkedIdentities.length === 0 && (
            <p className="text-sm text-slate-500">No social identities linked yet.</p>
          )}
          {linkedIdentities.map((identity) => (
            <div key={identity.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div className="flex items-center gap-3">
                {identity.provider === 'google' && <IconGoogle className="w-5 h-5 shrink-0 mr-4" />}
                {identity.provider === 'apple' && <IconApple className="w-5 h-5 shrink-0 mr-4 text-slate-950 dark:text-white" />}
                {identity.provider === 'github' && <IconGithub className="w-5 h-5 shrink-0 mr-4 text-slate-700 dark:text-slate-300" />}
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white capitalize">{identity.provider}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Linked: {new Date(identity.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <button
                onClick={() => unlinkIdentity(identity.id)}
                disabled={linkedIdentities.length === 1}
                className={`px-3 py-2 rounded text-sm font-medium transition-all ${
                  linkedIdentities.length === 1
                    ? 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-500 cursor-not-allowed opacity-50'
                    : 'bg-red-100 dark:bg-red-900/40 hover:bg-red-200 text-red-700 dark:text-red-300 border border-red-300'
                }`}
              >
                Unlink
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['google', 'apple', 'github'].map((provider) => (
            <button
              key={provider}
              onClick={() => linkAnotherAccount(provider)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium flex items-center justify-center capitalize"
            >
              {provider === 'google' && <IconGoogle className="w-4 h-4 inline-block mr-2" />}
              {provider === 'apple' && <IconApple className="w-4 h-4 inline-block mr-2 text-white" />}
              {provider === 'github' && <IconGithub className="w-4 h-4 inline-block mr-2 text-white" />}
              <span>+ Link {provider}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Linked Ecosystems */}
      <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Linked Ecosystems & Connected Apps</h2>
        <div className="space-y-4">
          {linkedAppsPaginated.length === 0 && (
            <p className="text-sm text-slate-500">No applications linked.</p>
          )}
          {linkedAppsPaginated.map((app) => {
            const isLocked = !!getAppLockInfo(app.client_id);
            return (
              <div key={app.id} className="flex items-start justify-between p-4 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{app.name}</h3>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {app.scopes?.map((scope) => (
                      <span key={scope} className="px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-xs rounded border border-indigo-300 dark:border-indigo-700">
                        {scope}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Connected on {new Date(app.created_at).toLocaleDateString()}</p>
                </div>
                <button
                  onClick={() => handleRevokeAccess(app.id, app.name, app.client_id)}
                  disabled={revokeLoading === app.id || isLocked}
                  className={`ml-4 px-4 py-2 rounded text-sm font-medium ${
                    isLocked
                      ? 'bg-slate-200 dark:bg-slate-700/50 text-slate-500 cursor-not-allowed'
                      : 'bg-red-100 dark:bg-red-900/40 hover:bg-red-200 text-red-700 border border-red-300'
                  }`}
                >
                  {isLocked ? 'Locked' : revokeLoading === app.id ? 'Revoking...' : 'Revoke'}
                </button>
              </div>
            );
          })}
        </div>
        
        {/* Pagination Linked Apps */}
        {linkedAppsTotalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button onClick={() => setLinkedAppsCurrentPage(p => Math.max(1, p - 1))} disabled={linkedAppsCurrentPage === 1} className="px-3 py-2 bg-slate-200 rounded text-sm disabled:opacity-50">Prev</button>
            <span className="text-sm dark:text-white">{linkedAppsCurrentPage} / {linkedAppsTotalPages}</span>
            <button onClick={() => setLinkedAppsCurrentPage(p => Math.min(linkedAppsTotalPages, p + 1))} disabled={linkedAppsCurrentPage === linkedAppsTotalPages} className="px-3 py-2 bg-slate-200 rounded text-sm disabled:opacity-50">Next</button>
          </div>
        )}
      </section>

      {/* Active Sessions */}
      <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Active Browser Sessions</h2>
        <div className="space-y-4">
          {activeSessions.length === 0 && <p className="text-sm text-slate-500">No active sessions found.</p>}
          {activeSessions.map((session) => (
            <div key={session.id || session.session_id} className="p-4 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{session.user_agent || 'Unknown Browser'}</h3>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">IP: {session.ip_address}</p>
                </div>
                <button
                  onClick={() => handleTerminateSession(session.session_id || session.id)}
                  disabled={terminateLoading === (session.session_id || session.id)}
                  className="ml-4 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded text-xs font-medium"
                >
                  {terminateLoading === (session.session_id || session.id) ? 'Terminating...' : 'Force Log Out'}
                </button>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Created: {new Date(session.created_at || session.loginTime).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Audit Log */}
      <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">Security Audit Log</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left px-4 py-3 text-slate-700 dark:text-slate-300 font-semibold">Action</th>
                <th className="text-left px-4 py-3 text-slate-700 dark:text-slate-300 font-semibold">IP Address</th>
                <th className="text-left px-4 py-3 text-slate-700 dark:text-slate-300 font-semibold">Browser (Agent)</th>
                <th className="text-left px-4 py-3 text-slate-700 dark:text-slate-300 font-semibold">Timestamp</th>
                <th className="text-left px-4 py-3 text-slate-700 dark:text-slate-300 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.map((entry, idx) => (
                <tr key={entry.id} className={`border-b border-slate-200 dark:border-slate-700/50 ${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-900/20' : ''}`}>
                  <td className="px-4 py-3 text-slate-900 dark:text-slate-200">{entry.action}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-mono text-xs">{entry.ip_address}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs truncate max-w-[150px]" title={entry.user_agent}>{entry.user_agent || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">{new Date(entry.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${entry.status.toLowerCase() === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {entry.status}
                    </span>
                  </td>
                </tr>
              ))}
              {auditLog.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-3 text-center text-slate-500">No security events found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Audit Log */}
        {auditLogTotalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button onClick={() => setAuditLogCurrentPage(p => Math.max(1, p - 1))} disabled={auditLogCurrentPage === 1} className="px-3 py-2 bg-slate-200 rounded text-sm disabled:opacity-50">Prev</button>
            <span className="text-sm dark:text-white">{auditLogCurrentPage} / {auditLogTotalPages}</span>
            <button onClick={() => setAuditLogCurrentPage(p => Math.min(auditLogTotalPages, p + 1))} disabled={auditLogCurrentPage === auditLogTotalPages} className="px-3 py-2 bg-slate-200 rounded text-sm disabled:opacity-50">Next</button>
          </div>
        )}
      </section>
    </div>
  );
}
